import { useQuery, useMutation, useQueryClient } from 'react-query';
import BudgetApi from '../api/budgetApi';

// Query keys
export const BUDGET_QUERY_KEYS = {
  VOTING_SETTINGS: 'budget-voting-settings',
  MY_VOTES: 'budget-my-votes',
  VOTE_DETAILS: 'budget-vote-details',
};

// Hook for loading vote form settings
export const useVotingSettings = (budgetId) => {
  return useQuery(
    [BUDGET_QUERY_KEYS.VOTING_SETTINGS, budgetId], 
    () => BudgetApi.loadVoteFormSettings(budgetId).then(response => {
      if (response.ok) {
        return response.data;
      }
      throw new Error('Failed to load voting settings');
    }),
    {
      enabled: !!budgetId,
    }
  );
};

// Hook for getting user's votes
export const useMyVotes = (enabled = true) => {
  return useQuery(
    BUDGET_QUERY_KEYS.MY_VOTES,
    () => BudgetApi.getMyVotes().then(response => {
      if (response.ok) {
        return response.data;
      }
      throw new Error('Failed to load votes');
    }),
    {
      enabled,
    }
  );
};

// Hook for getting vote details
export const useVoteDetails = (voteId, enabled = true) => {
  return useQuery(
    [BUDGET_QUERY_KEYS.VOTE_DETAILS, voteId],
    () => BudgetApi.getVoteDetails(voteId).then(response => {
      if (response.ok) {
        return response.data;
      }
      throw new Error('Failed to load vote details');
    }),
    {
      enabled: enabled && !!voteId,
    }
  );
};

// Hook for submitting a vote
export const useSubmitVote = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (voteData) => BudgetApi.vote(voteData).then(response => {
      if (response.ok) {
        // Check for specific vote errors in successful response (like Redux vote action)
        if (response.data && response.data.error) {
          // Create error object with specific vote error type
          const voteError = new Error('Vote submission error');
          voteError.voteErrorType = response.data.error;
          voteError.isVoteError = true;
          throw voteError;
        }
        return response.data;
      } else {
        // Handle HTTP error responses (like Redux vote action)
        const error = new Error('Vote submission failed');
        
        // Handle validation errors
        if (response.data && response.data.validationErrors && response.data.validationErrors.length > 0) {
          error.message = response.data.validationErrors[0].errorMessage;
          error.validationErrors = response.data.validationErrors;
        }
        // Handle general failure message
        else if (response.data && response.data.failure && response.data.failure.message) {
          error.message = response.data.failure.message;
        }
        // Handle direct error message
        else if (response.data && response.data.message) {
          error.message = response.data.message;
        }
        
        error.responseData = response.data;
        error.isHttpError = true;
        throw error;
      }
    }),
    {
      onSuccess: () => {
        // Invalidate my votes to refresh the list
        queryClient.invalidateQueries(BUDGET_QUERY_KEYS.MY_VOTES);
      },
    }
  );
};

// Hook for submitting a draft vote
export const useSubmitDraftVote = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (voteData) => BudgetApi.submitDraftVote(voteData).then(response => {
      if (response.ok) {
        return response.data;
      }
      throw new Error((response.data && response.data.message) || 'Failed to save draft vote');
    }),
    {
      onSuccess: () => {
        // Invalidate my votes to refresh the list
        queryClient.invalidateQueries(BUDGET_QUERY_KEYS.MY_VOTES);
      },
    }
  );
};

// Hook for phone number verification
export const usePhoneVerification = () => {
  return useMutation(
    ({ budgetId, phoneNumber, verificationMethodId }) => 
      BudgetApi.startPhoneNumberVerification(budgetId, phoneNumber, verificationMethodId)
        .then(response => {
          if (response.ok) {
            return { error: null, token: response.data.token };
          }
          if (!response.ok && response.data && response.data.message === 'Already voted') {
            return { error: { alreadyVoted: true } };
          }
          return { error: { other: true } };
        })
        .catch(() => {
          return { error: { other: true } };
        })
  );
};



// WEBPACK FOOTER //
// ./src/hooks/useBudgetVoting.js